/**
 * Tells an open tab that a newer version of the app has been installed, without reloading it.
 *
 * The service worker (vite-plugin-pwa, autoUpdate) already installs a new version, skips waiting, claims open pages
 * and drops old caches. What it cannot do is change the page that is already on screen: that tab keeps running the
 * old bundle until it is reloaded. Reloading it for the person would throw away anything they were typing, so this
 * only raises a flag; `UpdateAvailableBar` shows "A new version is available" and the person reloads when ready.
 *
 * A first-ever install has no previous controller, so it never raises the flag.
 */

export interface ServiceWorkerLike {
  controller: unknown;
  addEventListener(type: 'controllerchange', listener: () => void): void;
  getRegistration(): Promise<{ update(): Promise<unknown> } | undefined>;
}

export interface VisibilityLike {
  visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface UpdateWatcher {
  /** True once a new version has taken over from the one this tab loaded. */
  getSnapshot(): boolean;
  subscribe(listener: () => void): () => void;
  start(): void;
}

export function createUpdateWatcher(sw: ServiceWorkerLike | undefined, doc: VisibilityLike | undefined): UpdateWatcher {
  let ready = false;
  let started = false;
  const listeners = new Set<() => void>();

  const raise = () => {
    if (ready) return;
    ready = true;
    listeners.forEach((l) => l());
  };

  return {
    getSnapshot: () => ready,
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    start() {
      if (started || !sw) return;
      started = true;
      let hadController = !!sw.controller;

      sw.addEventListener('controllerchange', () => {
        if (!hadController) { hadController = true; return; } // first install: nothing old to replace
        raise();
      });

      const checkForUpdate = () => {
        sw.getRegistration().then((reg) => reg?.update()).catch(() => { /* offline or blocked: try next time */ });
      };
      doc?.addEventListener('visibilitychange', () => { if (doc.visibilityState === 'visible') checkForUpdate(); });
      checkForUpdate();
    },
  };
}

// The one watcher for this tab, wired to the browser.
export const updateWatcher: UpdateWatcher = createUpdateWatcher(
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? (navigator.serviceWorker as unknown as ServiceWorkerLike) : undefined,
  typeof document !== 'undefined' ? document : undefined,
);

export function watchForNewVersion(): void {
  updateWatcher.start();
}
